import React, { useMemo } from "react";

/**
 * Soft, dreamy cherry blossom petals — pure CSS + inline SVG.
 * Falls slowly with a gentle side-to-side drift. Sits behind page content (z-0).
 * Respects prefers-reduced-motion via the .animate-petal-fall fallback in CSS.
 */
const PETAL_SVG = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'><defs><radialGradient id='p' cx='50%' cy='42%' r='62%'><stop offset='0%' stop-color='#FFE4EC'/><stop offset='55%' stop-color='#F4A6BD'/><stop offset='100%' stop-color='#C96A87'/></radialGradient><radialGradient id='c' cx='50%' cy='50%' r='55%'><stop offset='0%' stop-color='#FFF4C2'/><stop offset='100%' stop-color='#E0A65A'/></radialGradient></defs><g transform='translate(32 32)'><g><path d='M0 -22 C-7 -18 -10 -10 -6 -4 C-3 0 3 0 6 -4 C10 -10 7 -18 0 -22 Z' fill='url(#p)' stroke='#B85A78' stroke-width='0.6' stroke-opacity='0.35'/></g><g transform='rotate(72)'><path d='M0 -22 C-7 -18 -10 -10 -6 -4 C-3 0 3 0 6 -4 C10 -10 7 -18 0 -22 Z' fill='url(#p)' stroke='#B85A78' stroke-width='0.6' stroke-opacity='0.35'/></g><g transform='rotate(144)'><path d='M0 -22 C-7 -18 -10 -10 -6 -4 C-3 0 3 0 6 -4 C10 -10 7 -18 0 -22 Z' fill='url(#p)' stroke='#B85A78' stroke-width='0.6' stroke-opacity='0.35'/></g><g transform='rotate(216)'><path d='M0 -22 C-7 -18 -10 -10 -6 -4 C-3 0 3 0 6 -4 C10 -10 7 -18 0 -22 Z' fill='url(#p)' stroke='#B85A78' stroke-width='0.6' stroke-opacity='0.35'/></g><g transform='rotate(288)'><path d='M0 -22 C-7 -18 -10 -10 -6 -4 C-3 0 3 0 6 -4 C10 -10 7 -18 0 -22 Z' fill='url(#p)' stroke='#B85A78' stroke-width='0.6' stroke-opacity='0.35'/></g><circle r='3.5' fill='url(#c)'/></g></svg>`;

const PETAL_DATA_URL = `data:image/svg+xml;utf8,${encodeURIComponent(PETAL_SVG)}`;

export default function SakuraPetals({ count = 7, className = "" }) {
  const petals = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => {
      const left = Math.random() * 100;
      const fallDuration = 22 + Math.random() * 14; // 22–36s — slow & gentle
      const swayDuration = 4 + Math.random() * 3;   // 4–7s — gentle drift
      const swayDistance = 14 + Math.random() * 18; // 14–32px sideways drift
      const delay = -Math.random() * fallDuration;
      const swayDelay = -Math.random() * swayDuration;
      const size = 18 + Math.random() * 14;         // 18–32px — larger so the flower shape reads
      const opacity = 0.55 + Math.random() * 0.25;   // 0.55–0.80 — more visible
      const rotateStart = Math.random() * 360;
      return { left, fallDuration, swayDuration, swayDistance, delay, swayDelay, size, opacity, rotateStart, key: i };
    });
  }, [count]);

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden z-0 ${className}`}
      data-testid="sakura-petals"
    >
      {petals.map((p) => (
        <span
          key={p.key}
          className="petal-fall"
          style={{
            left: `${p.left}%`,
            animationDuration: `${p.fallDuration}s`,
            animationDelay: `${p.delay}s`,
          }}
        >
          <span
            className="petal-sway"
            style={{
              animationDuration: `${p.swayDuration}s`,
              animationDelay: `${p.swayDelay}s`,
              ["--sway"]: `${p.swayDistance}px`,
            }}
          >
            <span
              className="petal-img"
              style={{
                width: `${p.size}px`,
                height: `${p.size}px`,
                opacity: p.opacity,
                backgroundImage: `url("${PETAL_DATA_URL}")`,
                transform: `rotate(${p.rotateStart}deg)`,
              }}
            />
          </span>
        </span>
      ))}
    </div>
  );
}
